'use client';

import Link from 'next/link';
import { Target, Pencil, Calendar } from 'lucide-react';
import { useEntityPermissions } from '@/lib/hooks/useEntityPermissions';
import { LazyImage } from '@/components/LazyImage';
import { formatEventDateRange, INITIATIVE_STAGE_OPTIONS } from '@sizzlenz/shared';

interface Initiative {
  id: number;
  name: string;
  type: string;
  status: string;
  visibility?: string;
  description?: string | null;
  image?: string | null;
  ownerId?: string;
  ownerType?: string;
  startDate?: string | null;
  endDate?: string | null;
}

interface InitiativesViewProps {
  initiatives: Initiative[];
  isLoading?: boolean;
  emptyMessage?: string;
  variant?: 'list' | 'compact';
  entityId?: number; // ID to check permissions for edit button
  showDates?: boolean; // Whether to show date range for each initiative
  /** When true, shows a 'Trade Only' badge on initiatives with visibility='trade' */
  showTradeOnlyBadge?: boolean;
  /** URL to navigate back to (appended as query param to initiative links) */
  backUrl?: string;
  /** Label for the back link (e.g. "Back to X Trade Hub") */
  backLabel?: string;
}

/**
 * Initiatives View Component
 * Reusable component for displaying a list of initiatives.
 * Handles special routing for quiz initiatives (/quizzes/[id]).
 * Shows edit button if user has permissions.
 */
export function InitiativesView({
  initiatives,
  isLoading = false,
  emptyMessage = 'No active initiatives, check back soon, or follow for notifications',
  variant = 'compact',
  entityId,
  showDates = false,
  showTradeOnlyBadge = false,
  backUrl,
  backLabel,
}: InitiativesViewProps) {
  const { hasPermission } = useEntityPermissions(entityId || 0);
  const canEditInitiatives = entityId && hasPermission('edit_initiatives');
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse flex gap-3">
            <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (initiatives.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Target className="w-12 h-12 mx-auto mb-2 text-gray-300" />
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {initiatives.map((initiative) => {
        // Determine the link based on initiative type
        // Quizzes are initiatives with type='quiz', so the quiz ID is the initiative ID
        const basePath =
          initiative.type === 'quiz'
            ? `/quizzes/${initiative.id}`
            : `/initiatives/${initiative.id}`;

        // Append back navigation query params if provided
        const queryParams = new URLSearchParams();
        if (backUrl) queryParams.set('back', backUrl);
        if (backLabel) queryParams.set('backLabel', backLabel);
        const queryString = queryParams.toString();
        const initiativeLink = queryString ? `${basePath}?${queryString}` : basePath;

        // Edit link - quizzes use /quizzes/[id]/edit, others use dashboard
        const editLink = entityId
          ? initiative.type === 'quiz'
            ? `/quizzes/${initiative.id}/edit`
            : `/entities/${entityId}/admin/initiatives/${initiative.id}/edit`
          : null;

        return (
          <div
            key={initiative.id}
            className="flex gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors items-center border border-gray-100"
          >
            <Link href={initiativeLink} className="flex gap-3 flex-1 min-w-0 cursor-pointer">
              {initiative.image ? (
                <LazyImage
                  src={initiative.image}
                  alt={initiative.name}
                  className="w-12 h-12 rounded-lg flex-shrink-0"
                  objectFit="cover"
                  fallback={
                    <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600">
                      <Target className="w-6 h-6" />
                    </div>
                  }
                />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-primary-50 flex-shrink-0 flex items-center justify-center text-primary-600">
                  <Target className="w-6 h-6" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{initiative.name}</p>
                <p className="text-sm text-gray-500 capitalize">
                  {initiative.type} •{' '}
                  {INITIATIVE_STAGE_OPTIONS.find((opt) => opt.value === initiative.status)?.label ||
                    initiative.status}
                </p>
                {showDates && (initiative.startDate || initiative.endDate) && (
                  <p className="text-sm flex items-center gap-1 mt-0.5">
                    <Calendar className="w-3 h-3" />
                    {formatEventDateRange(initiative.startDate, initiative.endDate)}
                  </p>
                )}
                {showTradeOnlyBadge && initiative.visibility === 'trade' && (
                  <span className="inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 mt-1">
                    Trade Only
                  </span>
                )}
              </div>
            </Link>

            {/* Edit button - always visible if user has edit permissions */}
            {canEditInitiatives && editLink && (
              <Link
                href={editLink}
                className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"
                title="Edit initiative"
                onClick={(e) => e.stopPropagation()}
              >
                <Pencil className="w-4 h-4 text-gray-600" />
              </Link>
            )}
          </div>
        );
      })}
    </div>
  );
}
